/* eslint-disable react/no-array-index-key */
import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { setModal } from 'modules/_shared/redux/modalActions';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import AddTradingOfferDocumentationModal from 'modules/trading/modals/AddTradingOfferDocumentation';
import { useTradingOfferOperationDetail } from '../context/TradingOfferOperationDetailContext';
import TradingOfferOperationDetailDocumentationItem from './TradingOfferOperationDetailDocumentationItem';
import { OperationDocumentRole } from '../types/TradingOfferOperationDetailContextType';

type DocItem = string | { _id: string; name?: string };

type TradingOfferOperationDetailDocumentationProps = {
  buyerDocuments: DocItem[];
  sellerDocuments: DocItem[];
  sellerName: string;
  equityMarketPurchaseContract?: DocItem;
};

const toIds = (docs: DocItem[]): string[] =>
  docs.map((d) => (typeof d === 'string' ? d : d._id));

const MAX_DOCUMENTS_PER_ROLE = 5;

const TradingOfferOperationDetailDocumentation: FC<
  TradingOfferOperationDetailDocumentationProps
> = ({ buyerDocuments, sellerDocuments, sellerName, equityMarketPurchaseContract }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { data, actions } = useTradingOfferOperationDetail();

  const buyerLabel =
    !data.isAdmin && data.userDocumentRole === 'buyer'
      ? t('BuyerYou')
      : t('Buyer');

  const canAddBuyerDocs =
    (data.isAdmin || data.userDocumentRole === 'buyer') &&
    (buyerDocuments?.length ?? 0) < MAX_DOCUMENTS_PER_ROLE;
  const canAddSellerDocs =
    (data.isAdmin || data.userDocumentRole === 'seller') &&
    (sellerDocuments?.length ?? 0) < MAX_DOCUMENTS_PER_ROLE;

  const openAddDocumentationModal = (initialRole: OperationDocumentRole) => {
    dispatch(
      setModal(
        <AddTradingOfferDocumentationModal
          operationId={data.operation!._id}
          societyId={data.societyId}
          isAdmin={data.isAdmin}
          userDocumentRole={data.userDocumentRole}
          initialDocumentRole={initialRole}
          sellerName={sellerName}
          buyerDocuments={toIds(buyerDocuments)}
          sellerDocuments={toIds(sellerDocuments)}
          onSuccess={actions.handleRefreshOperation}
        />
      )
    );
  };

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <h6 className="card-title mb-3">
          <em className="icon ni ni-file-docs text-primary mr-2" />
          {t('AssociatedDocumentation')}
        </h6>

        {/* Buyer Documents */}
        <div className="doc-seccion-titulo">
          <em className="icon ni ni-user mr-1" />
          {buyerLabel}
        </div>
        <div className="row g-2 mb-3">
          {buyerDocuments?.length ? (
            buyerDocuments.map((document, idx) => (
              <TradingOfferOperationDetailDocumentationItem
                key={`buyer-doc-${idx}`}
                document={document}
              />
            ))
          ) : (
            <TradingOfferOperationDetailDocumentationItem description="PendingDocumentationDesc" />
          )}
        </div>

        {canAddBuyerDocs && (
          <Button
            variant={ButtonVariants.PRIMARY}
            className="btn-block mt-4"
            outlineStyle
            onClick={() => openAddDocumentationModal('buyer')}
          >
            <em className="icon ni ni-plus mr-1" />
            {t('AddDocumentation')}
          </Button>
        )}

        {/* Seller Documents */}
        <div className="doc-seccion-titulo mt-4">
          <em className="icon ni ni-user mr-1" />
          {t('Seller')}
          {sellerName && ` (${sellerName})`}
        </div>
        <div className="row g-2 mb-3">
          {sellerDocuments?.length ? (
            sellerDocuments.map((document, idx) => (
              <TradingOfferOperationDetailDocumentationItem
                key={`seller-doc-${idx}`}
                document={document}
              />
            ))
          ) : (
            <TradingOfferOperationDetailDocumentationItem
              description={t('SellerDocsManagedBySociety')}
            />
          )}
        </div>
        <p className="text-soft fs-12px mb-0 mt-1">
          {t('SellerDocsManagedBySociety')}
        </p>

        {canAddSellerDocs && (
          <Button
            variant={ButtonVariants.PRIMARY}
            className="btn-block mt-4"
            outlineStyle
            onClick={() => openAddDocumentationModal('seller')}
          >
            <em className="icon ni ni-plus mr-1" />
            {t('AddDocumentation')}
          </Button>
        )}

        {/* Purchase contract */}
        {equityMarketPurchaseContract !== undefined && (
          <>
            <hr className="my-3" />
            <div className="doc-seccion-titulo mt-2">
              <em className="icon ni ni-file-check mr-1 text-success" />
              {t('EquityMarketPurchaseContractSection')}
            </div>
            <div className="row g-2 mt-1">
              <TradingOfferOperationDetailDocumentationItem
                document={equityMarketPurchaseContract}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TradingOfferOperationDetailDocumentation;
